import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { proposalsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import LoadingSpinner from '../LoadingSpinner';



const Proposals = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { } = useAuth();
  const { } = useSettings();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  });
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuProposal, setActionMenuProposal] = useState(null);

  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await proposalsAPI.getAll({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: searchTerm,
        status: statusFilter || undefined
        // Removed includeHistory to match templates pattern and improve performance
      });
      setProposals(response.data.proposals);
      setTotalCount(response.data.total || response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to fetch proposals');
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchProposals();
    
    // Set up interval to refresh data every 15 seconds
    const refreshInterval = setInterval(() => {
      fetchProposals();
    }, 15000);
    
    // Clean up interval on component unmount
    return () => clearInterval(refreshInterval);
  }, [fetchProposals]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleAddProposal = () => {
    navigate('/proposals/new');
  };

  const handleEditProposal = (proposal) => {
    navigate(`/proposals/edit/${proposal.id}`);
    handleCloseActionMenu();
  };

  const handleViewProposal = (proposal) => {
    navigate(`/proposals/view/${proposal.id}`);
    handleCloseActionMenu();
  };

  const handleDeleteProposal = (proposal) => {
    setProposalToDelete(proposal);
    setDeleteDialogOpen(true);
    handleCloseActionMenu();
  };

  const handleDownloadProposal = async (proposal) => {
    try {
      // This would typically generate and download a PDF
      toast.info('PDF download feature coming soon');
    } catch (error) {
      console.error('Error downloading proposal:', error);
      toast.error('Failed to download proposal');
    }
    handleCloseActionMenu();
  };

  const confirmDelete = async () => {
    try {
      await proposalsAPI.delete(proposalToDelete.id);
      toast.success('Proposal deleted successfully');
      fetchProposals();
    } catch (error) {
      console.error('Error deleting proposal:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete proposal';
      toast.error(errorMessage);
    } finally {
      setDeleteDialogOpen(false);
      setProposalToDelete(null);
    }
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuProposal(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'sent': return 'info';
      case 'opened': return 'primary';
      case 'viewed': return 'warning';
      case 'accepted': return 'success';
      case 'declined': return 'error';
      case 'rejected': return 'error';
      case 'expired': return 'secondary';
      default: return 'default';
    }
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <Box sx={{ flexGrow: 1 }} />
      <Button
        startIcon={<RefreshIcon />}
        onClick={fetchProposals}
        size="small"
        sx={{ mr: 1 }}
      >
        Refresh
      </Button>
    </GridToolbarContainer>
  );

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'customer',
      headerName: 'Customer',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="action" fontSize="small" />
          <Typography variant="body2">
            {params.row.customer?.companyName || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        // Create a comprehensive status history from available timestamps
        let statusHistory = params.row.statusHistory || [];
        
        // If no status history is available or it's empty, create a complete one from timestamps
        if (!statusHistory || statusHistory.length === 0) {
          statusHistory = [];
          
          // Always include draft status with createdAt timestamp
          statusHistory.push({
            status: 'draft',
            label: 'Draft',
            timestamp: params.row.createdAt
          });
          
          // Add other statuses based on their timestamps
          if (params.row.sentAt) {
            statusHistory.push({
              status: 'sent',
              label: 'Sent',
              timestamp: params.row.sentAt
            });
          }
          
          if (params.row.openedAt) {
            statusHistory.push({
              status: 'opened',
              label: 'Opened',
              timestamp: params.row.openedAt
            });
          }
          
          if (params.row.viewedAt) {
            statusHistory.push({
              status: 'viewed',
              label: 'Viewed',
              timestamp: params.row.viewedAt
            });
          }
          
          if (params.row.acceptedAt) {
            statusHistory.push({
              status: 'accepted',
              label: 'Accepted',
              timestamp: params.row.acceptedAt
            });
          }
          
          if (params.row.declinedAt) {
            statusHistory.push({
              status: 'declined',
              label: 'Declined',
              timestamp: params.row.declinedAt
            });
          }
          
          // Sort by timestamp
          statusHistory.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        }
        const tooltipContent = (
          <Box sx={{ 
            maxWidth: 300, 
            p: 1.5,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            borderRadius: 1
          }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: 'white', 
                fontWeight: 600, 
                mb: 1,
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                pb: 0.5
              }}
            >
              Status History
            </Typography>
            {statusHistory.map((item, index) => (
              <Box key={index} sx={{ 
                mb: index < statusHistory.length - 1 ? 1 : 0,
                p: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 0.5,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    mb: 0.5
                  }}
                >
                  {item.label}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.7rem'
                  }}
                >
                  {new Date(item.timestamp).toLocaleString('en-GB', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            ))}
          </Box>
        );
        
        return (
          <Tooltip
            title={statusHistory.length > 0 ? tooltipContent : 'No status history'}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'transparent',
                  maxWidth: 'none',
                  p: 0
                }
              }
            }}
          >
            <Chip
              label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
              color={getStatusColor(params.value)}
              size="small"
              sx={{ 
                fontSize: '0.75rem', 
                fontWeight: 600, 
                cursor: 'pointer',
                minWidth: '80px',
                textAlign: 'center',
                '& .MuiChip-label': {
                  textAlign: 'center',
                  width: '100%'
                }
              }}
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'validUntil',
      headerName: 'Valid Until',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Typography 
            variant="body2" 
            color={new Date(params.value) < new Date() ? 'error' : 'text.secondary'}
          >
            {new Date(params.value).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit', 
              year: 'numeric'
            })}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        )
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(params.value).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              color="info"
              onClick={() => handleViewProposal(params.row)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEditProposal(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download PDF">
            <IconButton
              size="small"
              onClick={() => handleDownloadProposal(params.row)}
            >
              <GetAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteProposal(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <DescriptionIcon color="primary" sx={{ fontSize: '2rem' }} />
            <Typography variant="h4" fontWeight={700}>
              Proposals
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage your business proposals
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddProposal}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          New Proposal
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <TextField
              placeholder="Search proposals by title or customer..."
              value={searchTerm}
              onChange={handleSearch}
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilter}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={proposals}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={totalCount}
            paginationMode="server"
            loading={loading}
            disableRowSelectionOnClick
            slots={{
              toolbar: CustomToolbar,
              loadingOverlay: () => <LoadingSpinner fullScreen={false} />
            }}
            autoHeight
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
                borderBottom: `2px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          />
        </Box>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The proposal and all associated data will be permanently deleted.
          </Alert>
          <Typography>
            Are you sure you want to delete the proposal <strong>{proposalToDelete?.title}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180
          }
        }}
      >
        <MenuItem onClick={() => handleViewProposal(actionMenuProposal)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View
        </MenuItem>
        <MenuItem onClick={() => handleEditProposal(actionMenuProposal)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDownloadProposal(actionMenuProposal)}>
          <GetAppIcon fontSize="small" sx={{ mr: 1 }} />
          Download PDF
        </MenuItem>
        <MenuItem 
          onClick={() => handleDeleteProposal(actionMenuProposal)}
          sx={{ color: theme.palette.error.main }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Proposals;